import { ReactNode, useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { PaginationData } from '../../constants/DataModels'
import './ServerTable.css'
import ViewMoreButton from './ViewMoreButton'
import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon, FunnelIcon } from '@heroicons/react/24/outline'
import { useSearchParams } from 'react-router-dom'
import { stripPrefix } from 'src/constants/ConversionUtils'
import TableFilters from 'src/components/Tables/TableFilters'

export interface TableProps<T extends Object> {
  name: string
  columns: Array<Column<T>>
  mobileColumns?: Array<Column<T>>
  onRequestData: (params: RequestDataParams) => Promise<DataModel<T>>
  filters: FilterConfig[]
  paramsPrefix?: string // used to differentiate table params when there are multiple tables in the page
  showPagination?: boolean
  showSort?: boolean
  viewMorePath?: string
  wrapperClass?: string
}

export interface RequestDataParams {
  offset: number
  limit: number
  filter: FilterState
  sort: SortingRule[]
}

export interface DataModel<T> {
  data: Array<T>
  pagination?: PaginationData
}

export interface SortingRule {
  id: string
  desc?: boolean
}

export interface FilterConfig {
  id: string
  name: string
  filterType: 'daterange' | 'select' | 'checkbox' | 'text'
  options?: string[]
  optionFormatter?: (option: string) => ReactNode
}

export interface FilterState {
  [id: string]: string
}

export interface Column<T> {
  id: string
  header: string | ReactNode
  canSort?: boolean
  cellRenderer?: (data: T) => ReactNode | string
}

export const DEFAULT_PAGE_SIZE = 10

const ServerTable = <T extends Record<string, any>>(props: TableProps<T>) => {
  const {
    name, columns, mobileColumns,
    onRequestData, filters,
    viewMorePath,
    paramsPrefix = '',
    showSort = true,
    showPagination = true,
    wrapperClass
  } = props

  const [dataModel, setDataModel] = useState<DataModel<T>>({ data: [] })
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(true)
  const [showFilters, setShowFilters] = useState(false)

  const prefix = !!paramsPrefix ? `${paramsPrefix}.` : ''
  const sortState = searchParams.get(`${prefix}sort`) || ''
  const pageLimitState = searchParams.get(`${prefix}page.limit`)
  const currentPageState = searchParams.get(`${prefix}page.current`)

  const pageLimit = pageLimitState ? Number(pageLimitState) : DEFAULT_PAGE_SIZE
  const currentPage = currentPageState ? Number(currentPageState) : 1
  const offset = (currentPage - 1) * pageLimit + 1
  const totalResults = dataModel.pagination?.total || 0
  const isEmptyTable = dataModel.data.length === 0
  const hasMobile = !!mobileColumns && mobileColumns.length > 0

  const relevantSearchEntries = [...searchParams.entries()].filter(([key]) =>
    key.startsWith(prefix)
  )
  const relevantSearchKey = JSON.stringify(relevantSearchEntries)
  useEffect(() => {
    const filterState: FilterState = Object.fromEntries(
      [...searchParams.entries()]
        .filter(([key]) => key.startsWith(prefix || ''))
        .map(([key, value]) => [key.replace(prefix || '', ''), value])
    )

    const sort: SortingRule[] = sortState
      ? sortState.split(',').map((s) => ({
        id: stripPrefix(s, ['-']),
        desc: s.startsWith('-'),
      }))
      : []

    setIsLoading(true)
    onRequestData({
      offset: (currentPage - 1) * pageLimit,
      limit: pageLimit,
      filter: filterState,
      sort,
    }).then((data) => {
      setDataModel(data)
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantSearchKey])

  const handlePageClick = (event: any) => {
    searchParams.set(`${prefix}page.current`, String(event.selected + 1))
    setSearchParams(searchParams)
  }

  const handlePageSize = (event: any) => {
    searchParams.set(`${prefix}page.limit`, event.target.value)
    searchParams.set(`${prefix}page.current`, '1')
    setSearchParams(searchParams)
  }

  const handleSorting = (id: string) => {
    const currentSort = sortState.split(',').filter(Boolean)
    const existingSort = currentSort.find(s => stripPrefix(s, ['-']) === id)
    let newSortState: string[]

    if (existingSort) {
      if (existingSort.startsWith('-')) {
        newSortState = currentSort.filter(s => stripPrefix(s, ['-']) !== id)
      } else {
        newSortState = currentSort.map(s =>
          stripPrefix(s, ['-']) === id ? `-${id}` : s
        )
      }
    } else {
      newSortState = [...currentSort, id]
    }

    searchParams.set(`${prefix}sort`, newSortState.join(','))
    setSearchParams(searchParams)
  }

  return (
    <div className={`${wrapperClass || 'table-wrapper'} ${hasMobile ? 'has-mobile-table' : ''}`}>
      <div className="table-header">
        <h4 className="table-title">{name}</h4>
        {showPagination && dataModel.pagination && (
          <div className="react-paginate-wrapper">
            <div className="page-info">
              {!isLoading && (
                <span>
                  Showing {offset} to {Math.min(offset + pageLimit - 1, totalResults)} of {totalResults} results
                </span>
              )}
            </div>
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              previousLabel="<"
              onPageChange={handlePageClick}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              pageCount={Math.ceil(totalResults / pageLimit)}
              forcePage={currentPage - 1}
              className="pagination-container"
              pageLinkClassName="pagination-index"
              previousLinkClassName="prev-button"
              nextLinkClassName="next-button"
              breakLinkClassName="break"
              activeClassName="active-button"
              disabledLinkClassName="disabled-button"
            />
            <div className="pagination">
              <div className="page-size-dropdown">
                <select value={pageLimit} onChange={handlePageSize}>
                  {[10, 25, 50].map((size) => (
                    <option key={size} value={size}>Show {size}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {
          filters.length > 0 && (
            <div className="filters-icon-container">
              <FunnelIcon className={`filters-icon ${showFilters ? 'selected' : ''}`}
                          onClick={() => setShowFilters(!showFilters)}
              />
            </div>
          )
        }

        {viewMorePath && (
          <div className="table-title">
            <ViewMoreButton path={viewMorePath} />
          </div>
        )}
      </div>

      <TableFilters filters={filters} searchParams={searchParams}
                    setSearchParams={setSearchParams} showFilters={showFilters}
                    filterPrefix={prefix}
      />

      {renderTable(isLoading, isEmptyTable, false, columns, sortState, showSort, handleSorting, dataModel)}
      {
        hasMobile && renderTable(isLoading, isEmptyTable, true, mobileColumns, sortState, showSort, handleSorting, dataModel)
      }
    </div>
  )
}

function renderTable<T>(isLoading: boolean, isEmptyTable: boolean, isMobile: boolean, columns: Array<Column<T>>, sortState: string, showSort: boolean, handleSorting: (id: string) => void, dataModel: DataModel<T>) {
  return <div className={`table-container ${isMobile ? 'mobile' : ''}`}>
    {isLoading ? (
      <div className="table-feedback">
        <span>Loading data...</span>
      </div>
    ) : isEmptyTable ? (
      <div className="table-feedback">
        <span>No data to display</span>
      </div>
    ) : (
      <table>
        <thead>
        <tr>
          {columns.map((column) => {
            const { id, header, canSort = false } = column
            const isSorted = sortState.includes(id)
            const isDesc = sortState.includes(`-${id}`)
            return (
              <th key={id} onClick={() => showSort && canSort && handleSorting(id)}>
                <div className={`header ${showSort && canSort ? 'sortable' : ''}`}>
                  {header}
                  {renderSortChevron(showSort && canSort, isSorted, isDesc)}
                </div>
              </th>
            )
          })}
        </tr>
        </thead>
        <tbody>
        {dataModel.data.map((row, index) => (
          <tr key={index}>
            {columns.map((column) => (
              <td key={column.id}>
                {column.cellRenderer ? column.cellRenderer(row) : (row as Record<string, any>)[column.id]}
              </td>
            ))}
          </tr>
        ))}
        </tbody>
      </table>
    )}
  </div>
}

const renderSortChevron = (canSort: boolean, isSorted: boolean, isDesc: boolean) => {
  if (!canSort) return null
  const shownChevron = isSorted ? (isDesc ? <ChevronDownIcon /> : <ChevronUpIcon />) :
    <ChevronUpDownIcon />
  return <span className="sort-chevron">{shownChevron}</span>
}

export default ServerTable
